/**
 * Topic filter state across Topic-wise, Recommended and Saved Questions.
 *
 * There is one topic level now (PIX-773). What used to be a subtopic is the
 * topic, and the old broad topics are categories the app never shows. Three
 * kinds of stale state survive from before that change, and each is handled
 * here as a pure function so the screens stay in step:
 *
 * 1. Bookmarked URLs carrying `subtopics=` -- the ids there are the new
 *    topics, so they move into `topics=`. The old `topics=` values were
 *    categories and are dropped.
 * 2. A topic id the subject's options no longer list -- a category uuid from
 *    an old link, or a topic from another subject. It is pruned rather than
 *    sent, so the list is never emptied by something the student cannot see.
 * 3. Filters stashed in sessionStorage by the previous build. The keys are
 *    versioned, so those entries are simply never read again.
 */

export const TOPIC_FILTER_STORAGE_VERSION = 'topics-v2';

/** The sessionStorage key for `name` under the current filter version. */
export function topicFilterStorageKey(name) {
  return `${TOPIC_FILTER_STORAGE_VERSION}:${name}`;
}

/**
 * Translate a pre-PIX-773 route query; returns `null` when nothing changes.
 *
 * `subtopics` present (even empty) wins: its values become `topics`, and any
 * old `topics` values -- categories -- are discarded with it.
 */
export function translateLegacyTopicQuery(query) {
  if (!query || !Object.prototype.hasOwnProperty.call(query, 'subtopics')) {
    return null;
  }
  const { subtopics, topics: _categories, ...rest } = query;
  const values = splitQueryValues(subtopics);
  return values.length ? { ...rest, topics: values.join(',') } : rest;
}

/**
 * Keep only the selected ids the loaded options know about.
 *
 * Returns the same array when nothing is dropped, so a caller can compare by
 * identity and skip a needless route write.
 */
export function pruneTopicSelection(selected, options) {
  if (!selected?.length) return selected ?? [];
  const known = new Set((options ?? []).map((option) => String(option.value)));
  const kept = selected.filter((value) => known.has(String(value)));
  return kept.length === selected.length ? selected : kept;
}

function splitQueryValues(value) {
  const raw = Array.isArray(value) ? value.join(',') : (value ?? '');
  return String(raw)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}
